import translations from "@/config/translations";
import useFetch from "@/hooks/useFetch";
import useLangStore from "@/hooks/useLangStore";
import usePopupStore from "@/hooks/usePopupStore";
import ActionsBar from "@/UI/components/ActionsBar";
import Table, { type TableColumn } from "@/UI/components/Table";
import TableCell from "@/UI/components/Table/TableCell";
import ConfirmPopup from "@/UI/popups/ConfirmPopup";
import { useEffect, useRef, useState } from "react";
import "./styles.css";

const PAGE_SIZE = 12;

interface Feedback {
    _id: string;
    customer?: {
        _id: string;
        name: string;
        phone: string;
        picture?: string;
    };
    product?: {
        _id: string;
        name: string | { lang: string; value: string }[];
        picture?: string;
        price?: number;
    };
    branch?: {
        _id: string;
        name: string | { lang: string; value: string }[];
        address?: string | { lang: string; value: string }[];
    };
    order?: {
        _id: string;
        number: string;
        totalPrice: number;
        status: string;
        date: string;
    };
    rate: number;
    feedback?: string;
    createdAt: string;
}

export default function FeedbacksScreen() {
    const { lang } = useLangStore();
    const t = translations[lang ?? "ar"];
    const { addComp, removeComp } = usePopupStore();
    const [page, setPage] = useState(1);
    const [deletingId, setDeletingId] = useState<string | null>(null);

    // ── Feedbacks fetch ─────────────────────────────────────────────────────────
    const { data, loading, invoke } = useFetch("feedbacks", { query: { page: 1, limit: PAGE_SIZE } });

    const isFirstRender = useRef(true);
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        invoke({ query: { page, limit: PAGE_SIZE } });
    }, [page]);

    const feedbacks: Feedback[] = data?.data?.feedbacks ?? [];
    const total: number = data?.data?.total ?? 0;

    // ── Delete fetch ────────────────────────────────────────────────────────────
    const { loading: deleting, status: deleteStatus, invoke: deleteInvoke } = useFetch("feedbacks", { method: "DELETE" }, false);

    const wasDeleting = useRef(false);
    useEffect(() => {
        if (deleting) {
            wasDeleting.current = true;
        } else if (wasDeleting.current) {
            wasDeleting.current = false;
            setDeletingId(null);
            if (deleteStatus === 200) invoke({ query: { page, limit: PAGE_SIZE } });
        }
    }, [deleting]);

    const handleDelete = (id: string) => {
        if (deleting) return;
        setDeletingId(id);
        deleteInvoke({ param: id });
    };

    const openDeleteConfirm = (feedback: Feedback) => {
        const name = "confirm-delete-feedback";
        removeComp(name);
        addComp({
            name,
            Component: ConfirmPopup,
            details: {
                message: t.feedbacks.deleteConfirm,
                icon: "/icons/delete-icon.svg",
                variant: "danger",
                onConfirm: () => handleDelete(feedback._id),
            },
            place: "center",
            anim: "scale",
            cover: "shaded",
            coverClose: true,
        });
    };

    // ── Helper Functions ────────────────────────────────────────────────────────
    const getLocalizedValue = (value: string | { lang: string; value: string }[] | undefined): string => {
        if (!value) return "—";
        if (typeof value === "string") return value;
        if (Array.isArray(value)) {
            const match = value.find((v) => v.lang === lang) ?? value[0];
            return match?.value ?? "—";
        }
        return "—";
    };

    const formatDate = (dateString: string): string => {
        if (!dateString) return "—";
        const date = new Date(dateString);
        return date.toLocaleDateString(lang === "ar" ? "ar-SA" : "en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    // ── Columns ─────────────────────────────────────────────────────────────────
    const columns: TableColumn<Feedback>[] = [
        {
            key: "customer",
            header: t.feedbacks.col.customer,
            width: "160px",
            render: (row) => (
                <div className="feedbacks-customer">
                    {row.customer?.picture ? (
                        <img src={row.customer.picture} alt={row.customer.name} className="feedbacks-customer-picture" />
                    ) : (
                        <div className="feedbacks-customer-placeholder">{row.customer?.name?.charAt(0) ?? "?"}</div>
                    )}
                    <TableCell.Text value={row.customer?.name || "—"} maxChars={18} />
                </div>
            ),
        },
        {
            key: "order",
            header: t.feedbacks.col.order,
            width: "110px",
            render: (row) => (row.order?.number ? <span className="feedbacks-order-number">#{row.order.number}</span> : "—"),
        },
        {
            key: "product",
            header: t.feedbacks.col.product,
            width: "140px",
            render: (row) => <TableCell.Text value={getLocalizedValue(row.product?.name)} maxChars={18} />,
        },
        {
            key: "branch",
            header: t.feedbacks.col.branch,
            width: "130px",
            render: (row) => <TableCell.Text value={getLocalizedValue(row.branch?.name)} maxChars={16} />,
        },
        {
            key: "rate",
            header: t.feedbacks.col.rate,
            width: "130px",
            render: (row) => (
                <div className="feedbacks-rating">
                    {[1, 2, 3, 4, 5].map((star) => (
                        <img
                            key={star}
                            src={star <= row.rate ? "/icons/star-active-icon.svg" : "/icons/star-inactive-icon.svg"}
                            alt=""
                            className="feedbacks-star-icon"
                        />
                    ))}
                </div>
            ),
        },
        {
            key: "feedback",
            header: t.feedbacks.col.feedback,
            width: "220px",
            render: (row) => <TableCell.Text value={row.feedback || "—"} maxChars={35} />,
        },
        {
            key: "date",
            header: t.feedbacks.col.date,
            width: "120px",
            render: (row) => <TableCell.Text value={formatDate(row.createdAt)} />,
        },
        {
            key: "actions",
            header: t.feedbacks.col.actions,
            width: "60px",
            render: (row) => (
                <TableCell.Actions
                    disabled={deletingId === row._id}
                    actions={[{ name: "delete", icon: "/icons/delete-icon.svg", handler: () => openDeleteConfirm(row), danger: true }]}
                />
            ),
        },
    ];

    const filterContent = <input className="actions-bar-search-input" placeholder={t.actionsBar.search} />;

    return (
        <div className="feedbacks-screen">
            <ActionsBar filterContent={filterContent} />
            <div className="feedbacks-screen-content">
                <Table
                    columns={columns}
                    data={feedbacks}
                    loading={loading}
                    rowKey="_id"
                    pagination={{ page, pageSize: PAGE_SIZE, total, onPageChange: setPage }}
                />
            </div>
        </div>
    );
}
